import { format, formatDistanceToNowStrict, isValid, parseISO } from 'date-fns';
import { useMemo } from 'react';

import { STRINGS } from '@/constants/strings';
import type { FamilyEvent } from '@/features/guardian/family-events';
import { useFamilyEvents } from '@/features/guardian/hooks/use-family-events';

const strings = STRINGS.guardian.kids.eventDetail;

function formatBookedAgo(bookedAt?: string): string | undefined {
  if (!bookedAt) return undefined;

  const date = parseISO(bookedAt);
  if (!isValid(date)) return undefined;

  return formatDistanceToNowStrict(date, { addSuffix: true });
}

export function getEventDetailLabels(event?: FamilyEvent) {
  if (!event) {
    return { dateLabel: undefined, bookedAgoLabel: undefined, bookingMetaLabel: undefined };
  }

  const date = parseISO(event.date);
  const dateLabel = isValid(date) ? format(date, 'EEEE, MMM d, yyyy') : undefined;
  const bookedAgoLabel = formatBookedAgo(event.bookedAt);
  const bookingMetaLabel = bookedAgoLabel
    ? strings.bookedAgo(event.bookedByLabel, bookedAgoLabel)
    : event.bookedByLabel;

  return { dateLabel, bookedAgoLabel, bookingMetaLabel };
}

/** Looks up one shared family event and derives labels consumed by Event Detail surfaces. */
export function useEventDetail(eventId: string) {
  const { events, isLoading, refetch } = useFamilyEvents();

  const event = useMemo(() => events.find(item => item.id === eventId), [eventId, events]);
  const { dateLabel, bookedAgoLabel, bookingMetaLabel } = useMemo(
    () => getEventDetailLabels(event),
    [event],
  );

  return { event, dateLabel, bookedAgoLabel, bookingMetaLabel, isLoading, refetch };
}
